import { useEffect } from 'react';

import {
  _todayKey,
  _loadTriviaHistory,
  _saveTriviaHistory,
  _computeStreak,
  _bestScore,
} from '../utils/triviaHelpers';

import {
  _computeGamificationFromStats,
  _normalizeGamiForUI,
} from '../utils/gamificationHelpers';

import {
  _saveUserStatsCache,
  _saveGamiCache,
} from '../utils/userStatsCache';

const useDevTriviaSeed = ({
  resolvedUid,
  setHistory,
  setStreak,
  setBest,
  setUserStats,
  setGami,
}) => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    try {
      window.__DEV_seedStreak = (n = 5) => {
        const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
        const days = clamp(Number(n) || 0, 1, 30);

        const today = _todayKey();
        const parts = today.split('-').map(Number);
        const base = new Date(parts[0], parts[1] - 1, parts[2]);

        const seeded = [];

        for (let i = days - 1; i >= 0; i--) {
          const d = new Date(base);
          d.setDate(d.getDate() - i);

          const key =
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

          seeded.push({
            day: key,
            score: 1,
            total: 3,
            ts: Date.now() - i * 1000,
          });
        }

        const prev = _loadTriviaHistory(resolvedUid);

        const map = new Map();

        [...prev, ...seeded].forEach((r) => {
          if (r?.day) map.set(r.day, r);
        });

        const next = [...map.values()].sort((a, b) =>
          a.day.localeCompare(b.day)
        );

        _saveTriviaHistory(resolvedUid, next);

        setHistory(next);

        const streak = _computeStreak(next);

        setStreak(streak);
        setBest(_bestScore(next));

        setUserStats((prevStats) => {
          const totalGames = new Set(next.map((r) => r.day)).size;
          const totalScore = next.reduce(
            (a, r) => a + (Number(r.score) || 0),
            0
          );

          const avgScore = totalGames ? totalScore / totalGames : 0;

          const optimistic = {
            ...(prevStats || {}),
            totalGames,
            totalScore,
            avgScore,
            lastPlayed: next.at(-1)?.day || null,
            streak: Math.max(Number(prevStats?.streak || 0), streak),
          };

          const gami = _normalizeGamiForUI(
            _computeGamificationFromStats(optimistic),
            optimistic
          );

          setGami(gami);

          _saveUserStatsCache(resolvedUid, optimistic);
          _saveGamiCache(resolvedUid, gami);

          return optimistic;
        });

        console.log(`[DEV] Seed aplicado (${days} días)`);
      };
    } catch {}

    return () => {
      try {
        delete window.__DEV_seedStreak;
      } catch {}
    };
  }, [
    resolvedUid,
    setHistory,
    setStreak,
    setBest,
    setUserStats,
    setGami,
  ]);
};

export default useDevTriviaSeed;